/**
 * PostHog browser bootstrap for the Hook shell.
 *
 * Initialized lazily after first paint (via `requestIdleCallback`) so analytics
 * never blocks Time-To-Interactive. Init is conditional on
 * `import.meta.env.VITE_POSTHOG_KEY` AND `import.meta.env.PROD` — same gate as
 * Sentry. Without env vars the entire module is silent no-op (downstream
 * consumers like `sw/posthogRelay.ts` and `install-prompt/capture.ts` already
 * defend with optional chaining).
 *
 * Multi-tenancy: every `app_opened` triggers `analytics.group('app', appId)`
 * from the AppLoader. PostHog group analytics segment all events by app
 * without per-app project setup. See ADR-019.
 *
 * `loaded` callback writes the posthog instance to `window.posthog` so:
 *   - SDK `createTrack()` (sdk/src/stubs/track.ts) finds it for bundle events
 *   - SW relay (sw/posthogRelay.ts) finds it for `sw_version_installed`
 *   - install-prompt (install-prompt/capture.ts) finds it for `pwa_*` events
 *
 * See docs/superpowers/specs/2026-04-25-posthog-multi-app-design.md.
 */
import posthog from 'posthog-js';
import { type TrackedEvent } from '@hook-sdk/sdk';

interface InitOpts {
  /** Called when posthog `loaded` fires (after `window.posthog` is populated). */
  onLoaded?: () => void;
}

let initialized = false;

export function initPostHog(opts: InitOpts = {}): void {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';
  if (!key || !import.meta.env.PROD) return;

  // Lazy init: wait for the browser idle window so the first paint isn't
  // blocked by ~30KB of posthog-js evaluation. Fallback to setTimeout(0) for
  // browsers without rIC (Safari < 16.4).
  const schedule =
    typeof (globalThis as any).requestIdleCallback === 'function'
      ? (cb: () => void) => (globalThis as any).requestIdleCallback(cb, { timeout: 2000 })
      : (cb: () => void) => setTimeout(cb, 0);

  schedule(() => {
    posthog.init(key, {
      api_host: host,
      // Free tier: only create person profiles after `identify()`. Anonymous
      // visitors don't count against the limit beyond raw events.
      person_profiles: 'identified_only',
      // LGPD: anonymize IP server-side. distinctId is the Hook user UUID
      // (no email/name in event properties).
      ip: false,
      // Hook is a SPA — disable auto pageview/pageleave; we capture explicit
      // `app_opened` from the AppLoader on every app mount instead.
      capture_pageview: false,
      capture_pageleave: false,
      // Autocapture is noisy and conflicts with the closed allowlist (ADR-011).
      // Every event must come from the SDK's createTrack or this wrapper's
      // analytics.capture below.
      autocapture: false,
      loaded: (ph) => {
        (window as unknown as { posthog: typeof ph }).posthog = ph;
        opts.onLoaded?.();
      },
    });
    initialized = true;
  });
}

/**
 * Trusted analytics surface for shell code (AppLoader, route handlers, etc).
 * Bundle code uses `useHook().track` which runs through the SDK validator —
 * shell code is trusted and uses this directly. All events here must still
 * be members of `ALLOWED_EVENTS` (sdk/src/analytics-events.ts) for dashboard
 * consistency, even though there's no runtime check on this side.
 */
export const analytics = {
  capture(event: TrackedEvent, props?: Record<string, unknown>): void {
    const ph = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    if (typeof ph?.capture !== 'function') return;
    try {
      ph.capture(event, props);
    } catch {
      /* noop */
    }
  },

  identify(distinctId: string, props?: Record<string, unknown>): void {
    const ph = (window as unknown as { posthog?: { identify?: (id: string, p?: Record<string, unknown>) => void } }).posthog;
    if (typeof ph?.identify !== 'function') return;
    try {
      ph.identify(distinctId, props);
    } catch {
      /* noop */
    }
  },

  group(groupType: 'app', groupKey: string, props?: Record<string, unknown>): void {
    const ph = (window as unknown as { posthog?: { group?: (t: string, k: string, p?: Record<string, unknown>) => void } }).posthog;
    if (typeof ph?.group !== 'function') return;
    try {
      ph.group(groupType, groupKey, props);
    } catch {
      /* noop */
    }
  },

  alias(alias: string): void {
    const ph = (window as unknown as { posthog?: { alias?: (a: string) => void } }).posthog;
    if (typeof ph?.alias !== 'function') return;
    try {
      ph.alias(alias);
    } catch {
      /* noop */
    }
  },

  optOut(): void {
    const ph = (window as unknown as { posthog?: { opt_out_capturing?: () => void } }).posthog;
    try {
      ph?.opt_out_capturing?.();
    } catch {
      /* noop */
    }
  },

  isReady(): boolean {
    return typeof (window as unknown as { posthog?: { capture?: unknown } }).posthog?.capture === 'function';
  },
};
